/**
 * ArchLang v0.10 — the style engine's **color casts + legend** (S14).
 *
 * A pure color engine consumed by `view-projection.ts`'s style-clause
 * orchestration. It turns the four render casts on the render-typed `color`
 * leaf into a per-node color result + a legend that NEVER lies (design §2):
 *
 *   - `colorize(<getter>)` → a value's **stable** palette swatch (`swatchFor`);
 *     one swatch legend entry per distinct value, plus a "no data" swatch for
 *     missing values.
 *   - `distinct(<getter>)` → the same, but assigned by index across THIS
 *     projection's sorted value set: maximum contrast, at the cost of stability.
 *   - `heat(<getter>, min, max)` → a sequential 2-color ramp; one gradient
 *     legend entry, plus a distinct "no data" entry for missing/non-numeric.
 *   - `css(<expr>)` → identity cast (the value IS the CSS color); a swatch per
 *     distinct color; an unparseable value → neutral + reported as `invalid`
 *     (the orchestrator lints it).
 *
 * The categorical casts emit palette NAMES, not hex — `@archlang/scene`'s
 * `paletteColor()` turns those into per-theme pixels for both backends. Only the
 * `heat` ramp interpolates concrete colors here, because a gradient is computed
 * rather than named.
 *
 * Engine-local by design: no app-shell dependency (the engine can't import
 * `aspectColors.ts`). The legend is engine-typed — S15's thin adapter maps it
 * onto the board's `NodeColoring`/`ColorLegendEntry`. Everything here is a pure
 * function of its inputs (sorted iteration, frozen ring) so the projection's
 * determinism guarantee holds.
 */

import { isCssColor, SWATCH_RING, swatchFor } from "./palette.js";

/** The two endpoints of the `heat` sequential ramp (cold → hot). */
const HEAT_COLD = "#2c7fb8";
const HEAT_HOT = "#e34a33";

/** The neutral color a node gets when it has no value under the cast getter —
 *  paired with a distinct "no data" legend entry so the legend never lies.
 *
 *  A palette ROLE, not a hex: engine holds names and semantics only, so the
 *  theme owns what "muted" looks like and a second theme cannot drift from a
 *  literal copied out of the dark one. Every consumer of `colorResult` already
 *  resolves through `paletteColor`, which is where this becomes pixels. */
export const NEUTRAL_NO_DATA = "muted";

// =============================================================================
// Legend spec (engine-typed)
// =============================================================================

export type ColorLegendKind = "swatch" | "gradient" | "noData";

/**
 * One legend row. `memberKeys` are QUALIFIED keys (the projection's universe
 * key) — S15 bridges them to scene ids via the existing `stableIds`/copy map.
 * A `swatch` row carries a flat `color`; a `gradient` row carries its ramp
 * endpoints + axis labels; a `noData` row is the neutral bucket.
 */
export interface ColorLegendEntry {
  readonly id: string;
  readonly label: string;
  /** Swatch / no-data fill. Absent on a gradient row (use `gradient`). */
  readonly color?: string;
  /** Present on a `gradient` row: the ramp endpoints + min/max axis labels. */
  readonly gradient?: {
    readonly from: string;
    readonly to: string;
    readonly minLabel: string;
    readonly maxLabel: string;
  };
  readonly kind: ColorLegendKind;
  /** Qualified keys of the shown nodes this row accounts for (sorted). */
  readonly memberKeys: readonly string[];
}

/** A cast's output: per-node colors (qualified key → color) + its legend rows. */
export interface ColorAssignment {
  readonly colors: ReadonlyMap<string, string>;
  readonly legend: readonly ColorLegendEntry[];
}

// =============================================================================
// colorize / distinct — the two categorical casts
// =============================================================================

/**
 * `colorize(<getter>)`: a value → its **stable** palette swatch.
 *
 * The color depends on the VALUE ALONE (`swatchFor`), never on which other
 * values are in the projection — so `Platform` is the same swatch on the base
 * board, in a filtered view, and in an export, and two surfaces showing the same
 * team can never disagree. Collisions past `SWATCH_RING.length` distinct values
 * are the accepted price; `distinct()` trades that stability for contrast.
 */
export function assignColorize(
  perNode: ReadonlyMap<string, string | undefined>,
  leafId: string
): ColorAssignment {
  return assignCategorical(perNode, leafId, (values) => new Map(values.map((v) => [v, swatchFor(v)])));
}

/**
 * `distinct(<getter>)`: distinct-value → categorical ring by index, for maximum
 * contrast across THIS projection's value set (values sorted, ring assigned by
 * index, wrapping).
 *
 * Set-relative by construction, which is exactly what `colorize` refuses to be:
 * add or filter out a value and the surviving nodes may all shift swatch. Worth
 * it only when the whole point is telling a small fixed set apart.
 */
export function assignDistinct(
  perNode: ReadonlyMap<string, string | undefined>,
  leafId: string
): ColorAssignment {
  return assignCategorical(
    perNode,
    leafId,
    (values) => new Map(values.map((v, i) => [v, SWATCH_RING[i % SWATCH_RING.length]!]))
  );
}

/** The shared body of the categorical casts: `assign` decides value → color,
 *  everything else (no-data bucket, sorted membership, legend) is common. */
function assignCategorical(
  perNode: ReadonlyMap<string, string | undefined>,
  leafId: string,
  assign: (sortedValues: readonly string[]) => ReadonlyMap<string, string>
): ColorAssignment {
  const distinct = [...new Set([...perNode.values()].filter((v): v is string => v !== undefined))].sort();
  const colorOf = assign(distinct);

  const colors = new Map<string, string>();
  const membersByValue = new Map<string, string[]>();
  const noData: string[] = [];
  for (const key of [...perNode.keys()].sort()) {
    const v = perNode.get(key);
    if (v === undefined) {
      colors.set(key, NEUTRAL_NO_DATA);
      noData.push(key);
      continue;
    }
    colors.set(key, colorOf.get(v)!);
    const list = membersByValue.get(v) ?? [];
    list.push(key);
    membersByValue.set(v, list);
  }

  const legend: ColorLegendEntry[] = distinct.map((v) => ({
    id: `${leafId}:${v}`,
    label: v,
    color: colorOf.get(v)!,
    kind: "swatch" as const,
    memberKeys: (membersByValue.get(v) ?? []).slice().sort(),
  }));
  if (noData.length > 0) {
    legend.push(noDataEntry(leafId, noData));
  }
  return { colors, legend };
}

// =============================================================================
// heat
// =============================================================================

/**
 * `heat(<getter>, min, max)`: a sequential 2-color ramp. `perNode` maps a
 * node's qualified key to its numeric value, or `undefined`/`NaN` for
 * missing/non-numeric (which land in the neutral "no data" bucket + a distinct
 * legend entry). One gradient legend row describes the ramp.
 */
export function assignHeat(
  perNode: ReadonlyMap<string, number | undefined>,
  min: number,
  max: number,
  label: string,
  leafId: string
): ColorAssignment {
  const colors = new Map<string, string>();
  const rampMembers: string[] = [];
  const noData: string[] = [];
  const span = max - min;
  for (const key of [...perNode.keys()].sort()) {
    const v = perNode.get(key);
    if (v === undefined || Number.isNaN(v)) {
      colors.set(key, NEUTRAL_NO_DATA);
      noData.push(key);
      continue;
    }
    const t = span === 0 ? 0 : clamp01((v - min) / span);
    colors.set(key, lerpHex(HEAT_COLD, HEAT_HOT, t));
    rampMembers.push(key);
  }
  const legend: ColorLegendEntry[] = [
    {
      id: `${leafId}:ramp`,
      label,
      kind: "gradient",
      gradient: { from: HEAT_COLD, to: HEAT_HOT, minLabel: String(min), maxLabel: String(max) },
      memberKeys: rampMembers.slice().sort(),
    },
  ];
  if (noData.length > 0) {
    legend.push(noDataEntry(leafId, noData));
  }
  return { colors, legend };
}

// =============================================================================
// css (identity cast)
// =============================================================================

/** `css(<expr>)`: the value IS the CSS color. Returns per-node colors + a
 *  swatch legend of the distinct valid colors, plus the set of unparseable
 *  values (`invalid`) so the orchestrator can lint each one. An unparseable /
 *  missing value → the neutral no-data color. */
export interface CssAssignment extends ColorAssignment {
  /** Distinct unparseable color strings encountered (sorted) — one lint each. */
  readonly invalid: readonly string[];
}

export function assignCss(
  perNode: ReadonlyMap<string, string | undefined>,
  leafId: string
): CssAssignment {
  const colors = new Map<string, string>();
  const membersByColor = new Map<string, string[]>();
  const invalid = new Set<string>();
  const noData: string[] = [];
  for (const key of [...perNode.keys()].sort()) {
    const raw = perNode.get(key);
    if (raw === undefined) {
      colors.set(key, NEUTRAL_NO_DATA);
      noData.push(key);
      continue;
    }
    if (!isCssColor(raw)) {
      invalid.add(raw);
      colors.set(key, NEUTRAL_NO_DATA);
      noData.push(key);
      continue;
    }
    colors.set(key, raw);
    const list = membersByColor.get(raw) ?? [];
    list.push(key);
    membersByColor.set(raw, list);
  }
  const distinct = [...membersByColor.keys()].sort();
  const legend: ColorLegendEntry[] = distinct.map((c) => ({
    id: `${leafId}:${c}`,
    label: c,
    color: c,
    kind: "swatch" as const,
    memberKeys: (membersByColor.get(c) ?? []).slice().sort(),
  }));
  if (noData.length > 0) {
    legend.push(noDataEntry(leafId, noData));
  }
  return { colors, legend, invalid: [...invalid].sort() };
}

// =============================================================================
// A literal / already-resolved CSS color applied to a set (the "styled" bucket)
// =============================================================================

/** A literal `color: "#f43"` (or bundle-supplied constant) applied to a node
 *  set — one flat color, one distinct "styled" legend entry (design §: an
 *  override drops into a distinct legend row so the legend never lies). */
export function assignStyled(keys: readonly string[], color: string, leafId: string): ColorAssignment {
  const colors = new Map<string, string>();
  for (const k of keys) colors.set(k, color);
  const legend: ColorLegendEntry[] =
    keys.length > 0
      ? [{ id: `${leafId}:styled`, label: color, color, kind: "swatch", memberKeys: keys.slice().sort() }]
      : [];
  return { colors, legend };
}

// =============================================================================
// CSS color recognition + interpolation
// =============================================================================

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

/** Interpolate two #rrggbb colors in sRGB. `t` is clamped to [0,1]. */
function lerpHex(a: string, b: string, t: number): string {
  const k = clamp01(t);
  const [ar, ag, ab] = parseHex(a);
  const [br, bg, bb] = parseHex(b);
  const r = Math.round(ar + (br - ar) * k);
  const g = Math.round(ag + (bg - ag) * k);
  const bl = Math.round(ab + (bb - ab) * k);
  return `#${toHex2(r)}${toHex2(g)}${toHex2(bl)}`;
}

function parseHex(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function toHex2(n: number): string {
  const c = Math.max(0, Math.min(255, n));
  return c.toString(16).padStart(2, "0");
}

function noDataEntry(leafId: string, keys: readonly string[]): ColorLegendEntry {
  return {
    id: `${leafId}:__nodata__`,
    label: "no data",
    color: NEUTRAL_NO_DATA,
    kind: "noData",
    memberKeys: keys.slice().sort(),
  };
}
